import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from "rxjs";
import { shareReplay, tap } from "rxjs/operators";

@Injectable()
export class ReferenceDataCacheService {
  private usersCache$ = new BehaviorSubject<any[]>([]);
  private usersCacheTime = 0;
  private firmDetailsCache$ = new BehaviorSubject<any>([]);
  private firmDetailsCacheTime = 0;
  private teamsForComboCache$ = new BehaviorSubject<any>([]);
  private teamsForComboCacheTime = 0;
  private rolesPermissionsCache = new Map<string, { data$: BehaviorSubject<any>, cacheTime: number }>();
  private CACHE_TTL = 20 * 60 * 1000; // 20 minutes

  constructor(
    private http: HttpClient
  ) { }

  getUsers(): Observable<any[]> {
    const now = Date.now();
    // Return cache if still fresh
    if (this.usersCacheTime && now - this.usersCacheTime < this.CACHE_TTL && this.usersCache$.value.length > 0 ) {
      return this.usersCache$.asObservable();
    }
    // Fetch and cache
    return this.http.get<any[]>('UserManagement/GetUsers').pipe(
      tap(users => {
        this.usersCache$.next(users);
        this.usersCacheTime = Date.now();
      }),
      shareReplay(1)
    );
  }

  getRolesPermissions(area?: string): Observable<any> {
    const now = Date.now();
    const cacheKey = area || '';
    const cacheEntry = this.rolesPermissionsCache.get(cacheKey);
    if (cacheEntry?.cacheTime && now - cacheEntry.cacheTime < this.CACHE_TTL) {
      return cacheEntry.data$.asObservable();
    }

    const rolesPermissionsCache$ = cacheEntry?.data$ || new BehaviorSubject<any>(null);

    let httpParams = new HttpParams();
    if (area) {
      httpParams = httpParams.set('area', area);
    }
    return this.http.get<any>('UserManagement/GetRolesPermissions', { params: httpParams }).pipe(
      tap(data => {
        rolesPermissionsCache$.next(data);
        this.rolesPermissionsCache.set(cacheKey, {
          data$: rolesPermissionsCache$,
          cacheTime: Date.now()
        });
      }),
      shareReplay(1)
    );
  }

  getTeamsForCombo(): Observable<any> {
    const now = Date.now();
    if (this.teamsForComboCacheTime && now - this.teamsForComboCacheTime < this.CACHE_TTL && this.teamsForComboCache$.value.length > 0) {
      return this.teamsForComboCache$.asObservable();
    }

    return this.http.get<any>('Teams/TeamsForCombo').pipe(
      tap(data => {
        this.teamsForComboCache$.next(data);
        this.teamsForComboCacheTime = Date.now();
      }),
      shareReplay(1)
    );
  }

  getFirmDetails(): Observable<any> {
    const now = Date.now();
    if (this.firmDetailsCacheTime && now - this.firmDetailsCacheTime < this.CACHE_TTL && this.firmDetailsCache$.value) {
      return this.firmDetailsCache$.asObservable();
    }

    return this.http.get<any>('AccountingFirm/GetFirmDetails').pipe(
      tap(data => {
        this.firmDetailsCache$.next(data);
        this.firmDetailsCacheTime = Date.now();
      }),
      shareReplay(1)
    );
  }

  invalidateUsers() {
    this.usersCache$.next([]);
    this.usersCacheTime = 0;
  }

  invalidateFirmDetails() {
    this.firmDetailsCache$.next([]);
    this.firmDetailsCacheTime = 0;
  }

  invalidateRolesPermissions() {
    this.rolesPermissionsCache.clear();
  }

  invalidateTeamsForCombo() {
    this.teamsForComboCache$.next([]);
    this.teamsForComboCacheTime = 0;
  }

  // Clears all caches at once
  invalidate() {
    this.invalidateUsers();
    this.invalidateFirmDetails();
    this.invalidateRolesPermissions();
    this.invalidateTeamsForCombo();
  }
}